/**
 * SpeakEngineProvider — the single global mount point for the speak engine.
 *
 * Sits high in the tree (WorldLayout) so ANY child component can call
 * useSpeakEngine().openCapture({ preset, onResult, ... }) and get a modal
 * without having to render one itself. Only one session is active at a
 * time — a second openCapture() call replaces the previous session (its
 * previous onResult never fires).
 *
 * The provider intentionally owns very little state:
 *   - `session` (the active capture request, or null)
 *   - a monotonically-increasing `openCount` used purely so React sees a
 *     new key when the same preset is re-opened, which fully remounts
 *     SpeakEngineModal (fresh recorder, fresh transcript, fresh phase).
 *
 * Everything else lives inside SpeakEngineModal, keeping the provider
 * side-effect-free and cheap to include even on pages that never use it.
 */

import React, { createContext, useCallback, useMemo, useRef, useState } from 'react';
import SpeakEngineModal from './SpeakEngineModal';
import { getPreset } from './presets';

export const SpeakEngineContext = createContext(null);

/**
 * Props:
 *   userId        — Firebase uid of the "world" being edited (target namespace).
 *   currentUserId — the actively logged-in user's uid (for authoring metadata).
 *   canEdit       — whether the current user can write to userId's world.
 *
 * When userId is missing (e.g. still bootstrapping), the provider stays
 * silent — openCapture() throws so misuse fails loud.
 */
export default function SpeakEngineProvider({ userId, currentUserId, canEdit = true, children }) {
  const [session, setSession] = useState(null);
  const [openCount, setOpenCount] = useState(0);
  // Track resolved preset separately so we don't re-resolve on every render.
  const sessionRef = useRef(session);
  sessionRef.current = session;

  const closeCapture = useCallback(() => {
    setSession((prev) => {
      if (prev?.onCancel) {
        // Fire onCancel only if the session ended without a confirmed result.
        // The modal calls setResolved(true) via a helper on session before
        // closing — until we have that, treat manual close as cancel.
        try { prev.onCancel(); } catch (_) { /* noop */ }
      }
      return null;
    });
  }, []);

  const openCapture = useCallback((opts) => {
    if (!userId) throw new Error('SpeakEngineProvider: cannot open capture — userId is not ready yet.');
    if (!opts) throw new Error('SpeakEngine.openCapture: options required');

    const preset = getPreset(opts.preset);
    setSession({
      preset,
      context: opts.context || {},
      onResult: opts.onResult || null,
      onCancel: opts.onCancel || null,
      openedAt: Date.now(),
    });
    setOpenCount((n) => n + 1);
  }, [userId]);

  const value = useMemo(() => ({
    openCapture,
    closeCapture,
    isActive: !!session,
    activePresetId: session?.preset?.id || null,
    userId,
  }), [openCapture, closeCapture, session, userId]);

  return (
    <SpeakEngineContext.Provider value={value}>
      {children}
      {session && (
        <SpeakEngineModal
          key={openCount}
          session={session}
          userId={userId}
          currentUserId={currentUserId}
          canEdit={canEdit}
          onClose={closeCapture}
        />
      )}
    </SpeakEngineContext.Provider>
  );
}
